import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { useNavigate, Link } from 'react-router-dom'
import { useBrand } from '../hooks/useBrand'

// ── Brand tokens — standalone public page, no CSS vars ──
const NAVY   = '#0B1F3A'
const TEAL   = '#2DD4B0'
const TEAL_D = '#1BA888'

const STATUS_CONFIG = {
  received:     { label: 'Received',     dot: '#60A5FA', bg: '#EFF6FF', text: '#1D4ED8' },
  pending:      { label: 'Pending',      dot: '#FCD34D', bg: '#FFFBEB', text: '#92400E' },
  confirmed:    { label: 'Confirmed',    dot: '#FDE68A', bg: '#FEF3C7', text: '#92400E' },
  processing:   { label: 'Processing',   dot: '#5EEAD4', bg: '#F0FDFA', text: '#0F766E' },
  with_courier: { label: 'With Courier', dot: '#C4B5FD', bg: '#F5F3FF', text: '#6D28D9' },
  shipped:      { label: 'Shipped',      dot: '#86EFAC', bg: '#F0FDF4', text: '#15803D' },
  closed:       { label: 'Delivered',    dot: '#6EE7B7', bg: '#ECFDF5', text: '#065F46' },
  return:       { label: 'Returned',     dot: '#FCA5A5', bg: '#FEF2F2', text: '#B91C1C' },
}

// Order status progression — for the mini timeline
const STATUS_ORDER = ['received','confirmed','processing','with_courier','shipped','closed']

const SELLER_COLORS = ['#6366F1','#EC4899','#F97316','#22C55E','#3B82F6','#8B5CF6','#06B6D4','#F43F5E']

let pdfGenerating = false
function generateReceiptPDF(order, seller, brand) {
  if (pdfGenerating) return
  pdfGenerating = true
  import('jspdf').then(({ default: jsPDF }) => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' })
    const pw  = doc.internal.pageSize.getWidth()
    let y = 20
    const center = (t, yy, s = 10) => { doc.setFontSize(s); doc.text(t, pw / 2, yy, { align: 'center' }) }
    const line   = (yy) => { doc.setDrawColor(220,220,220); doc.line(20, yy, pw - 20, yy) }
    const row    = (l, v, yy) => { doc.setFontSize(9); doc.setFont('helvetica','normal'); doc.setTextColor(120,120,120); doc.text(l, 20, yy); doc.setTextColor(40,40,40); doc.text(String(v), pw-20, yy, { align:'right' }) }

    doc.setFont('helvetica','bold'); doc.setFontSize(18); doc.setTextColor(11,31,58); center(brand.name, y, 18); y += 6
    doc.setFont('helvetica','normal'); doc.setFontSize(9); doc.setTextColor(120,120,120); center(brand.website, y, 9); y += 10; line(y); y += 8
    doc.setFont('helvetica','bold'); doc.setFontSize(14); doc.setTextColor(20,20,20); center(seller?.shop_name || 'Seller', y, 14); y += 6
    if (seller?.phone) { doc.setFont('helvetica','normal'); doc.setFontSize(9); doc.setTextColor(100,100,100); center(seller.phone, y, 9); y += 5 }
    if (seller?.verification_status === 'verified') { doc.setFontSize(9); doc.setTextColor(22,163,74); center('✓ Verified Seller', y, 9); y += 5 }
    y += 4; line(y); y += 8
    doc.setFont('helvetica','bold'); doc.setFontSize(11); doc.setTextColor(80,80,80); center('RECEIPT', y, 11); y += 6
    doc.setFont('helvetica','bold'); doc.setFontSize(12); doc.setTextColor(11,31,58); center(order.order_code, y, 12); y += 5
    doc.setFont('helvetica','normal'); doc.setFontSize(9); doc.setTextColor(120,120,120); center(new Date(order.created_at).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'}), y, 9); y += 10; line(y); y += 8
    doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(80,80,80); doc.text('PRODUCT', 20, y); y += 6
    doc.setFont('helvetica','normal'); doc.setFontSize(10); doc.setTextColor(20,20,20); doc.text(order.product_name || order.product_code || 'Product', 20, y); y += 5
    if (order.variant_name) { doc.setFontSize(9); doc.setTextColor(120,120,120); doc.text('Variant: '+order.variant_name, 20, y); y += 5 }
    const unitTotal = Number(order.unit_price||0)*Number(order.quantity||1)
    row('Qty: '+order.quantity+' × '+Number(order.unit_price||0).toLocaleString(), unitTotal.toLocaleString(), y); y += 5
    row('Delivery charge', Number(order.delivery_charge||0).toLocaleString(), y); y += 6; line(y); y += 6
    doc.setFont('helvetica','bold'); doc.setFontSize(11); doc.setTextColor(20,20,20); doc.text('Total', 20, y); doc.setTextColor(11,31,58); doc.text(Number(order.total_amount||0).toLocaleString(), pw-20, y, { align:'right' }); y += 10
    line(y); y += 8; doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(80,80,80); doc.text('PAYMENT', 20, y); y += 6
    row('Method', order.payment_method||'N/A', y); y+=5; row('Status', order.payment_status||'N/A', y); y+=5
    y+=3; line(y); y+=8; doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(80,80,80); doc.text('DELIVERY', 20, y); y+=6
    row('Courier', order.courier||'N/A', y); y+=5; if (order.tracking_number) { row('Tracking', order.tracking_number, y); y+=5 }
    y+=5; line(y); y+=8; doc.setFont('helvetica','bold'); doc.setFontSize(11); doc.setTextColor(45,212,176); center('Thank you for shopping!', y, 11); y+=6
    doc.setFont('helvetica','normal'); doc.setFontSize(9); doc.setTextColor(120,120,120); center('with '+(seller?.shop_name||'the seller'), y, 9); y+=10
    doc.setFontSize(7); doc.setTextColor(160,160,160); const dis = 'This receipt is generated by '+brand.name+' on behalf of the seller. All matters are between buyer and seller.'; const disL = doc.splitTextToSize(dis, pw-40); doc.text(disL, 20, y); y+=disL.length*4+5; line(y); y+=6
    doc.setFontSize(8); doc.setTextColor(11,31,58); center('Powered by '+brand.name+' · '+brand.website, y, 8)
    doc.save('receipt-'+order.order_code+'.pdf')
    pdfGenerating = false
  }).catch(() => { pdfGenerating = false })
}

// ── Status timeline component ──
function StatusTimeline({ status }) {
  if (status === 'return') return null
  const idx = STATUS_ORDER.indexOf(status)
  if (idx < 0) return null
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '10px' }}>
      {STATUS_ORDER.map((s, i) => {
        const done    = i <= idx
        const current = i === idx
        const sc      = STATUS_CONFIG[s]
        return (
          <div key={s} style={{ display: 'flex', alignItems: 'center', flex: i < STATUS_ORDER.length - 1 ? 1 : 'none' }}>
            <div title={sc.label} style={{ width: current ? '10px' : '8px', height: current ? '10px' : '8px', borderRadius: '50%', background: done ? (current ? TEAL : '#A7F3D0') : '#E5E7EB', border: current ? '2px solid ' + TEAL_D : 'none', flexShrink: 0, transition: 'all 0.2s' }} />
            {i < STATUS_ORDER.length - 1 && (
              <div style={{ flex: 1, height: '2px', background: i < idx ? '#A7F3D0' : '#E5E7EB', margin: '0 2px' }} />
            )}
          </div>
        )
      })}
      <span style={{ fontSize: '10px', color: '#6B7280', marginLeft: '6px', whiteSpace: 'nowrap' }}>{STATUS_CONFIG[status]?.label}</span>
    </div>
  )
}

export default function CustomerPortal() {
  const navigate    = useNavigate()
  const { brand }   = useBrand()

  const [account, setAccount]     = useState(null)
  const [orders, setOrders]       = useState([])
  const [sellers, setSellers]     = useState({})
  const [loading, setLoading]     = useState(true)
  const [activeTab, setActiveTab] = useState('active')
  const [signingOut, setSigningOut] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showProfile, setShowProfile] = useState(false)
  const [pdfToast, setPdfToast]   = useState(false)
  const [expandedOrder, setExpandedOrder] = useState(null)

  useEffect(() => { checkAuth() }, []) // eslint-disable-line

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { navigate('/customer/login'); return }
    const { data: acc } = await supabase.from('customer_accounts').select('*').eq('user_id', user.id).single()
    if (!acc) { navigate('/customer/login'); return }
    setAccount(acc)
    await fetchOrders(acc.phone)
  }

  const fetchOrders = async (phone) => {
    if (!phone) { setLoading(false); return }
    const variants = [phone]
    if (phone.startsWith('0') && phone.length === 11) variants.push('+880' + phone.slice(1))
    if (phone.startsWith('+880')) variants.push('0' + phone.slice(4))

    let customers = []
    for (const v of variants) {
      const { data } = await supabase.from('customers').select('id, seller_id').eq('phone', v)
      if (data?.length > 0) customers = [...customers, ...data]
    }
    const unique = customers.filter((c,i,a) => a.findIndex(x => x.id === c.id) === i)
    if (!unique.length) { setLoading(false); return }

    const customerIds = unique.map(c => c.id)
    const sellerIds   = [...new Set(unique.map(c => c.seller_id))]
    const csMap = {}; unique.forEach(c => { csMap[c.id] = c.seller_id })

    const { data: ords } = await supabase.from('orders').select('*, customers(name, phone)')
      .in('customer_id', customerIds).order('created_at', { ascending: false })
    const { data: sels } = await supabase.from('sellers')
      .select('id, shop_name, facebook_url, phone, logo_url, district, country, verification_status')
      .in('id', sellerIds)

    const selMap = {}; sels?.forEach(s => { selMap[s.id] = s })
    setSellers(selMap)
    setOrders((ords || []).map(o => ({ ...o, seller: selMap[csMap[o.customer_id]] })))
    setLoading(false)
  }

  const handleSignOut = async () => {
    setSigningOut(true); await supabase.auth.signOut(); navigate('/customer/login')
  }

  const downloadReceipt = (order, seller) => {
    generateReceiptPDF(order, seller, brand)
    setPdfToast(true); setTimeout(() => setPdfToast(false), 2500)
  }

  const activeOrders    = orders.filter(o => !['closed','return'].includes(o.status))
  const pastOrders      = orders.filter(o =>  ['closed','return'].includes(o.status))
  const totalSpent      = orders.filter(o => o.status !== 'return').reduce((s,o) => s + Number(o.total_amount||0), 0)
  const sym             = orders[0]?.seller?.seller_currency === 'USD' ? '$' : '৳'

  const spendBySeller = Object.values(sellers).map((s,i) => {
    const ords = orders.filter(o => o.seller?.id === s.id && o.status !== 'return')
    return { name: s.shop_name, id: s.id, spent: ords.reduce((a,o) => a + Number(o.total_amount||0), 0), count: ords.length, color: SELLER_COLORS[i % SELLER_COLORS.length] }
  }).sort((a,b) => b.spent - a.spent)

  const filterOrders = (list) => {
    if (!searchQuery) return list
    const q = searchQuery.toLowerCase()
    return list.filter(o => o.order_code?.toLowerCase().includes(q) || o.product_name?.toLowerCase().includes(q) || o.seller?.shop_name?.toLowerCase().includes(q))
  }

  const displayOrders = filterOrders(activeTab === 'active' ? activeOrders : pastOrders)

  // ── LOADING ──
  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '14px', background: '#F8FAFC', fontFamily: 'Space Grotesk, -apple-system, sans-serif' }}>
      <div style={{ width: '40px', height: '40px', borderRadius: '50%', border: '3px solid ' + TEAL, borderTopColor: 'transparent', animation: 'cp-spin 0.7s linear infinite' }} />
      {brand.logo_url
        ? <img src={brand.logo_url} alt={brand.name} style={{ height: '24px', objectFit: 'contain', opacity: 0.5 }} />
        : <div style={{ fontSize: '13px', fontWeight: 600, color: '#9CA3AF' }}>{brand.name}</div>
      }
      <div style={{ fontSize: '12px', color: '#9CA3AF' }}>Loading your orders...</div>
      <style>{`@keyframes cp-spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', fontFamily: 'Space Grotesk, -apple-system, sans-serif', paddingBottom: '32px' }}>

      {/* ── Sticky header ── */}
      <div style={{ position: 'sticky', top: 0, zIndex: 40, background: NAVY, padding: '10px 16px', boxShadow: '0 2px 12px rgba(11,31,58,0.3)' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {brand.logo_url
              ? <img src={brand.logo_url} alt={brand.name} style={{ height: '28px', objectFit: 'contain' }} />
              : <div style={{ fontSize: '16px', fontWeight: 700, color: '#fff' }}>{brand.name}</div>
            }
            <div style={{ width: '1px', height: '18px', background: 'rgba(255,255,255,0.15)' }} />
            <div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#fff' }}>{account?.name || 'My Orders'}</div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)' }}>{account?.phone}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {orders.length > 0 && (
              <div style={{ padding: '3px 9px', borderRadius: '20px', background: TEAL, fontSize: '11px', fontWeight: 700, color: NAVY }}>{orders.length}</div>
            )}
            <button onClick={handleSignOut} disabled={signingOut}
              style={{ padding: '5px 10px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', fontSize: '11.5px', fontWeight: 600, cursor: 'pointer' }}>
              {signingOut ? '...' : 'Sign out'}
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '16px 16px 0' }}>

        {/* ── Summary strip ── */}
        <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #E5E7EB', padding: '14px 18px', marginBottom: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
          {[
            { label: 'Total orders',  value: orders.length,         color: NAVY },
            { label: 'Active',        value: activeOrders.length,   color: TEAL_D },
            { label: 'Total spent',   value: sym + Math.round(totalSpent).toLocaleString(), color: '#6366F1' },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'center', padding: '8px 0' }}>
              <div style={{ fontSize: '20px', fontWeight: 800, color: s.color, letterSpacing: '-0.5px' }}>{s.value}</div>
              <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── Spend by seller ── */}
        {spendBySeller.length > 1 && (
          <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #E5E7EB', padding: '14px 18px', marginBottom: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', marginBottom: '12px' }}>Spending by seller</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {spendBySeller.slice(0, 5).map(s => (
                <div key={s.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                      <span style={{ fontSize: '12px', color: '#374151', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '160px' }}>{s.name}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '11px', color: '#9CA3AF' }}>{s.count} orders</span>
                      <span style={{ fontSize: '12.5px', fontWeight: 700, color: '#111827' }}>{sym}{Math.round(s.spent).toLocaleString()}</span>
                    </div>
                  </div>
                  <div style={{ height: '5px', borderRadius: '3px', background: '#F3F4F6', overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: '3px', background: s.color, width: totalSpent > 0 ? (s.spent / totalSpent * 100) + '%' : '0%', transition: 'width 0.4s ease' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Search ── */}
        <div style={{ position: 'relative', marginBottom: '10px' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by order code, product or seller..."
            style={{ width: '100%', padding: '11px 14px 11px 36px', borderRadius: '12px', border: '1px solid #E5E7EB', background: '#fff', fontSize: '14px', outline: 'none', color: '#111827', fontFamily: 'inherit', boxSizing: 'border-box', transition: 'border-color 0.15s' }}
            onFocus={e => e.target.style.borderColor = TEAL}
            onBlur={e => e.target.style.borderColor = '#E5E7EB'} />
        </div>

        {/* ── Tabs ── */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          {[
            { key: 'active', label: 'Active orders', count: activeOrders.length },
            { key: 'past',   label: 'Past orders',   count: pastOrders.length },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{ flex: 1, padding: '10px', borderRadius: '12px', border: activeTab === tab.key ? 'none' : '1px solid #E5E7EB', background: activeTab === tab.key ? NAVY : '#fff', color: activeTab === tab.key ? '#fff' : '#6B7280', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', transition: 'all 0.15s', fontFamily: 'inherit' }}>
              {tab.label}
              {tab.count > 0 && (
                <span style={{ padding: '1px 7px', borderRadius: '20px', background: activeTab === tab.key ? 'rgba(255,255,255,0.15)' : '#F3F4F6', color: activeTab === tab.key ? '#fff' : '#9CA3AF', fontSize: '11px', fontWeight: 700 }}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Orders list ── */}
        {orders.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #E5E7EB', padding: '40px 24px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.2" strokeLinecap="round" style={{ margin: '0 auto 14px', display: 'block' }}><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
            <div style={{ fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>No orders found</div>
            <div style={{ fontSize: '13px', color: '#9CA3AF', marginBottom: '16px', lineHeight: 1.6 }}>
              Your orders appear here when sellers record them with your phone number: <strong style={{ color: '#374151' }}>{account?.phone}</strong>
            </div>
            <Link to="/track" style={{ display: 'inline-block', padding: '10px 20px', borderRadius: '10px', background: NAVY, color: '#fff', fontSize: '13px', fontWeight: 600, textDecoration: 'none' }}>
              Track by phone number →
            </Link>
          </div>
        ) : displayOrders.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #E5E7EB', padding: '32px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: '13px', color: '#9CA3AF' }}>
              {searchQuery ? `No orders match "${searchQuery}"` : activeTab === 'active' ? 'No active orders right now' : 'No past orders yet'}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
            {displayOrders.map(order => {
              const sc      = STATUS_CONFIG[order.status] || { label: order.status, dot: '#D1D5DB', bg: '#F9FAFB', text: '#6B7280' }
              const isExpanded = expandedOrder === order.id
              const waLink  = order.seller?.phone ? 'https://wa.me/' + order.seller.phone.replace(/\D/g,'') : null

              return (
                <div key={order.id} style={{ background: '#fff', borderRadius: '16px', border: '1px solid #E5E7EB', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>

                  {/* Card header */}
                  <div style={{ padding: '14px 16px', cursor: 'pointer' }} onClick={() => setExpandedOrder(isExpanded ? null : order.id)}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <div>
                        <div style={{ fontSize: '13.5px', fontWeight: 700, color: NAVY, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.02em', marginBottom: '2px' }}>{order.order_code}</div>
                        <div style={{ fontSize: '12px', color: '#9CA3AF' }}>
                          {order.seller?.shop_name}
                          {order.seller?.verification_status === 'verified' && <span style={{ marginLeft: '5px', color: TEAL_D, fontWeight: 600 }}>✓</span>}
                          {' · '}{new Date(order.created_at).toLocaleDateString('en-BD', { day:'numeric', month:'short', year:'numeric' })}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexShrink: 0, marginLeft: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '3px 9px', borderRadius: '20px', background: sc.bg }}>
                          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: sc.dot, flexShrink: 0 }} />
                          <span style={{ fontSize: '11px', fontWeight: 600, color: sc.text, whiteSpace: 'nowrap' }}>{sc.label}</span>
                        </div>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="2" strokeLinecap="round" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s', flexShrink: 0 }}><polyline points="6 9 12 15 18 9"/></svg>
                      </div>
                    </div>

                    {/* Status timeline — active orders only */}
                    {!['closed','return'].includes(order.status) && <StatusTimeline status={order.status} />}

                    {/* Product info */}
                    <div style={{ padding: '10px 12px', borderRadius: '10px', background: '#F8FAFC', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {order.product_name || order.product_code}
                        </div>
                        {order.variant_name && <div style={{ fontSize: '11.5px', color: '#9CA3AF' }}>{order.variant_name}</div>}
                        <div style={{ fontSize: '11.5px', color: '#9CA3AF', marginTop: '2px' }}>Qty: {order.quantity}</div>
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: 800, color: NAVY, flexShrink: 0, marginLeft: '10px' }}>
                        {sym}{Number(order.total_amount||0).toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div style={{ borderTop: '1px solid #F3F4F6', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>

                      {/* Tracking */}
                      {order.tracking_number && (
                        <div style={{ padding: '10px 12px', borderRadius: '10px', background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: '#1D4ED8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Tracking</div>
                          <div style={{ fontSize: '13px', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, color: '#1E40AF', marginBottom: '4px' }}>{order.tracking_number}</div>
                          {order.courier && <div style={{ fontSize: '11.5px', color: '#3B82F6' }}>{order.courier}</div>}
                          {order.tracking_url && (
                            <a href={order.tracking_url} target="_blank" rel="noreferrer"
                              style={{ display: 'inline-block', marginTop: '6px', fontSize: '12px', color: '#2563EB', fontWeight: 600, textDecoration: 'none' }}>
                              Track on {order.courier} →
                            </a>
                          )}
                        </div>
                      )}

                      {/* Delivery info */}
                      {(order.expected_delivery || order.payment_method) && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                          {order.expected_delivery && (
                            <div style={{ padding: '8px 10px', borderRadius: '8px', background: '#F8FAFC' }}>
                              <div style={{ fontSize: '10.5px', color: '#9CA3AF', marginBottom: '2px' }}>Expected delivery</div>
                              <div style={{ fontSize: '12.5px', fontWeight: 600, color: '#374151' }}>{order.expected_delivery}</div>
                            </div>
                          )}
                          {order.payment_method && (
                            <div style={{ padding: '8px 10px', borderRadius: '8px', background: '#F8FAFC' }}>
                              <div style={{ fontSize: '10.5px', color: '#9CA3AF', marginBottom: '2px' }}>Payment</div>
                              <div style={{ fontSize: '12.5px', fontWeight: 600, color: '#374151' }}>{order.payment_method}</div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Notes */}
                      {order.notes && (
                        <div style={{ padding: '8px 12px', borderRadius: '8px', background: '#FFFBEB', border: '1px solid #FDE68A', fontSize: '12.5px', color: '#92400E' }}>
                          {order.notes}
                        </div>
                      )}

                      {/* Action buttons */}
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => downloadReceipt(order, order.seller)}
                          style={{ flex: 1, padding: '10px', borderRadius: '10px', border: '1px solid #E5E7EB', background: '#F9FAFB', color: '#374151', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontFamily: 'inherit' }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                          Receipt PDF
                        </button>
                        {waLink && (
                          <a href={waLink} target="_blank" rel="noreferrer"
                            style={{ flex: 1, padding: '10px', borderRadius: '10px', border: '1px solid #BBF7D0', background: '#F0FDF4', color: '#15803D', fontSize: '12.5px', fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="#15803D"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                            WhatsApp seller
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* ── Quick links ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
          <Link to="/track" style={{ background: '#fff', borderRadius: '14px', border: '1px solid #E5E7EB', padding: '16px', textAlign: 'center', textDecoration: 'none', display: 'block', transition: 'border-color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = TEAL}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#E5E7EB'}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={NAVY} strokeWidth="1.5" strokeLinecap="round" style={{ margin: '0 auto 8px', display: 'block' }}><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/></svg>
            <div style={{ fontSize: '12.5px', fontWeight: 600, color: '#111827' }}>Track by phone</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>Active orders only</div>
          </Link>
          <Link to="/sellers" style={{ background: '#fff', borderRadius: '14px', border: '1px solid #E5E7EB', padding: '16px', textAlign: 'center', textDecoration: 'none', display: 'block', transition: 'border-color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = TEAL}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#E5E7EB'}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={NAVY} strokeWidth="1.5" strokeLinecap="round" style={{ margin: '0 auto 8px', display: 'block' }}><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            <div style={{ fontSize: '12.5px', fontWeight: 600, color: '#111827' }}>Seller directory</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>Find verified sellers</div>
          </Link>
        </div>

        {/* ── Account info — collapsible ── */}
        <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #E5E7EB', overflow: 'hidden', marginBottom: '14px' }}>
          <button onClick={() => setShowProfile(o => !o)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>Your account</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" style={{ transform: showProfile ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          {showProfile && (
            <div style={{ padding: '0 16px 14px', borderTop: '1px solid #F3F4F6' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
                {[
                  { label: 'Name',  value: account?.name },
                  { label: 'Email', value: account?.email },
                  { label: 'Phone', value: account?.phone },
                ].map(({ label, value }) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', borderRadius: '8px', background: '#F9FAFB' }}>
                    <span style={{ fontSize: '12px', color: '#9CA3AF' }}>{label}</span>
                    <span style={{ fontSize: '12.5px', fontWeight: 600, color: '#374151' }}>{value || '—'}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '10px', padding: '9px 12px', borderRadius: '8px', background: '#EFF6FF', border: '1px solid #BFDBFE', fontSize: '12px', color: '#1D4ED8', lineHeight: 1.5 }}>
                Orders are matched by your phone number. Make sure sellers save: <strong>{account?.phone}</strong>
              </div>
            </div>
          )}
        </div>

        <div style={{ textAlign: 'center', fontSize: '11.5px', color: '#9CA3AF', paddingBottom: '8px' }}>
          {brand.name} · {brand.website} · Customer portal — free forever
        </div>
      </div>

      {/* ── PDF toast ── */}
      {pdfToast && (
        <div style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', background: NAVY, color: '#fff', padding: '10px 18px', borderRadius: '20px', fontSize: '13px', fontWeight: 600, zIndex: 100, display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.25)' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
          Downloading receipt...
        </div>
      )}

      <style>{`
        @keyframes cp-spin { to { transform: rotate(360deg) } }
        * { -webkit-tap-highlight-color: transparent; }
        @media(max-width:480px) {
          [data-summary] { grid-template-columns: 1fr 1fr 1fr; }
        }
      `}</style>
    </div>
  )
}